import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchKPIDashboard, fetchKPICategories, fetchKPIRanking, fetchKPIScores, createKPIScore, calculateKPI } from '../store/slices/kpiSlice';
import { fetchOrganizations } from '../store/slices/organizationsSlice';
import { kpiAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { Loading, ConfirmDialog } from '../components/common';
import { CURRENT_YEAR, QUARTERS, YEARS, YEARS_ } from '../utils/constants';
import toast from 'react-hot-toast';
import { ChartBarIcon, TrophyIcon, PlusIcon, CalculatorIcon, PencilIcon, TrashIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const KPI = () => {
  const dispatch = useDispatch();
  const { dashboard, categories, ranking, loading } = useSelector((state) => state.kpi);
  const { organizations } = useSelector((state) => state.organizations);
  const { user } = useSelector((state) => state.auth);
  
  const [year, setYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({ organization: '', category: '', score: 0, notes: '' });
  const [categoryFormData, setCategoryFormData] = useState({ name: '', code: '', max_score: 10, description: '', order: 0 });

  useEffect(() => {
    dispatch(fetchKPIDashboard());
    dispatch(fetchKPICategories());
    dispatch(fetchOrganizations());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchKPIRanking({ year, quarter }));
    dispatch(fetchKPIScores({ period_year: year, period_quarter: quarter }));
  }, [dispatch, year, quarter]);

  const handleAddScore = async (e) => {
    e.preventDefault();
    try {
      await dispatch(createKPIScore({ ...formData, period_year: year, period_quarter: quarter })).unwrap();
      toast.success('Ball qo\'shildi!');
      setShowScoreModal(false);
      setFormData({ organization: '', category: '', score: 0, notes: '' });
      dispatch(fetchKPIRanking({ year, quarter }));
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleCalculate = async () => {
    try {
      await dispatch(calculateKPI({ year, quarter })).unwrap();
      toast.success('KPI hisoblandi!');
      dispatch(fetchKPIRanking({ year, quarter }));
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCategory) {
        await kpiAPI.updateCategory(selectedCategory.id, categoryFormData);
        toast.success('Kategoriya yangilandi!');
      } else {
        await kpiAPI.createCategory(categoryFormData);
        toast.success('Kategoriya yaratildi!');
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      dispatch(fetchKPICategories());
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleEditCategory = (cat) => {
    setSelectedCategory(cat);
    setCategoryFormData({
      name: cat.name,
      code: cat.code || '',
      max_score: cat.max_score,
      description: cat.description || '',
      order: cat.order || 0
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async () => {
    try {
      await kpiAPI.deleteCategory(selectedCategory.id);
      toast.success('Kategoriya o\'chirildi!');
      setShowDeleteModal(false);
      setSelectedCategory(null);
      dispatch(fetchKPICategories());
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const resetCategoryForm = () => {
    setSelectedCategory(null);
    setCategoryFormData({ name: '', code: '', max_score: 10, description: '', order: 0 });
  };

  const isAdmin = user?.role === 'admin';

  if (loading && !dashboard) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">KPI Ko'rsatkichlari</h1>
          <p className="text-gray-500">{year}-yil {quarter}-chorak</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select className="select w-auto" value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
            {YEARS_.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="select w-auto" value={quarter} onChange={(e) => setQuarter(parseInt(e.target.value))}>
            {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
          </select>
          {isAdmin && (
            <>
              <button onClick={() => setShowScoreModal(true)} className="btn-primary">
                <PlusIcon className="w-5 h-5" /> Ball qo'shish
              </button>
              <button onClick={handleCalculate} className="btn-secondary">
                <CalculatorIcon className="w-5 h-5" /> Hisoblash
              </button>
            </>
          )}
        </div>
      </div>

      {isAdmin && dashboard && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card text-center">
            <p className="text-4xl font-bold text-blue-600">{dashboard.avg_score?.toFixed(1) || 0}</p>
            <p className="text-gray-500">O'rtacha ball</p>
          </div>
          <div className="card text-center">
            <p className="text-4xl font-bold text-green-600">{dashboard.total_organizations || 0}</p>
            <p className="text-gray-500">Tashkilotlar soni</p>
          </div>
          <div className="card text-center">
            <p className="text-4xl font-bold text-purple-600">100</p>
            <p className="text-gray-500">Maksimal ball</p>
          </div>
        </div>
      )}

      {!isAdmin && dashboard?.my_score !== undefined && (
        <div className="card">
          <div className="text-center py-6">
            <p className="text-6xl font-bold text-blue-600">{dashboard.my_score}</p>
            <p className="text-xl text-gray-500 mt-2">Sizning KPI ballingiz</p>
            {dashboard.my_rank && <p className="text-gray-400 mt-1">Reyting: #{dashboard.my_rank}</p>}
            <div className="mt-6 max-w-md mx-auto">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div className="bg-blue-600 h-4 rounded-full transition-all" style={{ width: `${Math.min((dashboard.my_score || 0), 100)}%` }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">100 balldan</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-blue-600" />
            Baholash kategoriyalari
          </h2>
          {isAdmin && (
            <button onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }} className="btn-outline btn-sm">
              <PlusIcon className="w-4 h-4" /> Kategoriya qo'shish
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((cat) => (
            <div key={cat.id} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-800">{cat.name}</p>
                  {cat.code && <p className="text-xs text-gray-500">{cat.code}</p>}
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => handleEditCategory(cat)} className="p-1 hover:bg-gray-200 rounded">
                      <PencilIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    <button onClick={() => { setSelectedCategory(cat); setShowDeleteModal(true); }} className="p-1 hover:bg-gray-200 rounded">
                      <TrashIcon className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-blue-600 mt-2">{cat.max_score} ball</p>
              {cat.description && <p className="text-xs text-gray-500 mt-1">{cat.description}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrophyIcon className="w-5 h-5 text-yellow-500" />
          OTMlar reytingi
        </h2>
        {ranking.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header w-16">#</th>
                  <th className="table-header">Tashkilot</th>
                  <th className="table-header text-right">Ball</th>
                  <th className="table-header w-40">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ranking.map((item, index) => (
                  <tr key={item.id} className={index < 3 ? 'bg-yellow-50' : ''}>
                    <td className="table-cell">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-400 text-white' :
                        index === 1 ? 'bg-gray-300 text-gray-800' :
                        index === 2 ? 'bg-orange-400 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>{index + 1}</span>
                    </td>
                    <td className="table-cell font-medium">{item.organization_short_name || item.organization_name}</td>
                    <td className="table-cell text-right font-bold text-blue-600">{item.total_score}</td>
                    <td className="table-cell">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${item.total_score}%` }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Ma'lumot topilmadi</p>
        )}
      </div>

      <Modal isOpen={showScoreModal} onClose={() => setShowScoreModal(false)} title="Ball qo'shish" size="md">
        <form onSubmit={handleAddScore} className="space-y-4">
          <div className="input-group">
            <label className="label">Tashkilot *</label>
            <select className="select" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} required>
              <option value="">Tanlang</option>
              {organizations.map(org => <option key={org.id} value={org.id}>{org.short_name || org.name}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="label">Kategoriya *</label>
            <select className="select" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required>
              <option value="">Tanlang</option>
              {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name} (max: {cat.max_score})</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="label">Ball *</label>
            <input type="number" min="0" max="100" className="input" value={formData.score} onChange={(e) => setFormData({ ...formData, score: parseFloat(e.target.value) })} required />
          </div>
          <div className="input-group">
            <label className="label">Izoh</label>
            <textarea className="textarea" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowScoreModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">Qo'shish</button>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)} title={selectedCategory ? 'Kategoriyani tahrirlash' : 'Yangi kategoriya'} size="md">
        <form onSubmit={handleCategorySubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Nomi *</label>
            <input type="text" className="input" value={categoryFormData.name} onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Kod</label>
              <input type="text" className="input" value={categoryFormData.code} onChange={(e) => setCategoryFormData({ ...categoryFormData, code: e.target.value })} placeholder="Masalan: KPI-01" />
            </div>
            <div className="input-group">
              <label className="label">Maksimal ball *</label>
              <input type="number" min="1" max="100" className="input" value={categoryFormData.max_score} onChange={(e) => setCategoryFormData({ ...categoryFormData, max_score: parseInt(e.target.value) })} required />
            </div>
          </div>
          <div className="input-group">
            <label className="label">Tavsif</label>
            <textarea className="textarea" value={categoryFormData.description} onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })} />
          </div>
          <div className="input-group">
            <label className="label">Tartib raqami</label>
            <input type="number" min="0" className="input" value={categoryFormData.order} onChange={(e) => setCategoryFormData({ ...categoryFormData, order: parseInt(e.target.value) })} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowCategoryModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedCategory ? 'Saqlash' : 'Yaratish'}</button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
        onConfirm={handleDeleteCategory}
        title="Kategoriyani o'chirish" 
        message={`"${selectedCategory?.name}" kategoriyasini o'chirishni xohlaysizmi?`} 
        confirmText="O'chirish" 
      />
    </div>
  );
};

export default KPI;
